// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Authority-filing vocabulary, and the legacy spellings stored data still holds.
 *
 * fix-sprint-20260905-2135-followup-2 residue (run 10), R-46. Three persisted
 * values were named after one receiving authority's abbreviation:
 *
 *   - the breach-notification status written when a submission is recorded;
 *   - the audit action the dossier-filing and breach-notification services
 *     write for the same act;
 *   - the audit action the cross-border filing service writes when an
 *     authority's receipt is recorded.
 *
 * New writes use the capability-shaped values below. Stored data is not
 * rewritten here, and the two kinds are treated differently on purpose:
 *
 *   - **The status** is a working field a register filters, counts and gates on,
 *     so a stored legacy value is renamed by the idempotent start-up migration
 *     `migrations/fix-20260905-2135-authority-filing-status-rename.ts` (the precedent is
 *     `62.27.0-consent-gap-attribute-rename.ts`). Until it has run on a
 *     replica — and for any reader that meets a row it has not reached — every
 *     reader accepts both spellings through `breachNotificationStatusSpellings`
 *     and `canonicalBreachNotificationStatus`.
 *   - **Audit actions are never rewritten.** An audit row is a record of what
 *     was written at the time, and the immutable-audit-trail module anchors
 *     rows by their content, so rewriting one would make its proof fail. A
 *     filter for an action is widened to every spelling of it instead —
 *     `auditActionSpellings` — so historical events stay findable under either
 *     name.
 *
 * This is the ONE place the legacy spellings live. A reader that needs one
 * imports it from here; nothing else in `src/` spells them.
 *
 * ## Why the R-46 check does not report these literals
 *
 * `authority_abbreviation` in `agent-os/scripts/lint-framework-hardcoding.mjs`
 * reads an abbreviation standing alone or as a camelCase segment. It does not
 * read snake_case tokens, because on this tree those are overwhelmingly
 * persisted vocabulary that cannot be renamed by an edit, and the two R-46
 * debt registers only ever shrink. That blind spot is stated in the pattern's
 * own docblock. Re-measure the population it leaves unread with:
 *
 *     grep -rnE "(^|[^A-Za-z0-9])mps_|_mps([^A-Za-z0-9]|$)" src --include=*.ts --include=*.tsx \
 *       | grep -v __tests__ | grep -v /i18n/
 */
/** The breach-notification status a recorded submission writes. */
export const BREACH_NOTIFICATION_SUBMITTED_STATUS = 'submitted_to_authority';
/**
 * Stored breach-notification statuses written before the rename, mapped to the
 * value that replaced each. Own-property lookups only (see
 * `canonicalBreachNotificationStatus`).
 */
export const LEGACY_BREACH_NOTIFICATION_STATUS_ALIASES = Object.freeze({
    submitted_to_mps: BREACH_NOTIFICATION_SUBMITTED_STATUS,
});
/** The audit actions authority-filing services write. */
export const AUTHORITY_FILING_AUDIT_ACTIONS = Object.freeze({
    /** `CrossBorderFilingService.recordAuthorityReceipt`. */
    CROSS_BORDER_RECEIPT_RECORDED: 'cross_border_filing.authority_receipt_recorded',
    /**
     * `DossierFilingService.submitToAuthority` and the breach-notification
     * submission. The same act on two records; each service writes it to its own
     * audit collection.
     */
    SUBMITTED_TO_AUTHORITY: 'submitted_to_authority',
});
/**
 * Stored audit actions written before the rename, mapped to the action that
 * replaced each. Read-side only: historical rows keep the action they were
 * written with.
 */
export const LEGACY_AUTHORITY_FILING_AUDIT_ACTION_ALIASES = Object.freeze({
    'cross_border_filing.mps_receipt_recorded': AUTHORITY_FILING_AUDIT_ACTIONS.CROSS_BORDER_RECEIPT_RECORDED,
    submitted_to_mps: AUTHORITY_FILING_AUDIT_ACTIONS.SUBMITTED_TO_AUTHORITY,
});
/**
 * The status a register should reason about: the replacement for a legacy
 * stored value, otherwise the value itself. Own-property lookup, so a status
 * that happens to equal an `Object.prototype` member is never mapped.
 */
export function canonicalBreachNotificationStatus(status) {
    return Object.hasOwn(LEGACY_BREACH_NOTIFICATION_STATUS_ALIASES, status)
        ? LEGACY_BREACH_NOTIFICATION_STATUS_ALIASES[status]
        : status;
}
/** Every stored spelling of a breach-notification status, canonical first. */
export function breachNotificationStatusSpellings(status) {
    const canonical = canonicalBreachNotificationStatus(status);
    const legacy = Object.keys(LEGACY_BREACH_NOTIFICATION_STATUS_ALIASES).filter((k) => LEGACY_BREACH_NOTIFICATION_STATUS_ALIASES[k] === canonical);
    return [canonical, ...legacy];
}
/**
 * Every stored spelling of an audit action, canonical first. A filter on
 * either the new or the legacy name finds rows written under both; any other
 * action is returned alone.
 */
export function auditActionSpellings(action) {
    const canonical = Object.hasOwn(LEGACY_AUTHORITY_FILING_AUDIT_ACTION_ALIASES, action)
        ? LEGACY_AUTHORITY_FILING_AUDIT_ACTION_ALIASES[action]
        : action;
    const legacy = Object.keys(LEGACY_AUTHORITY_FILING_AUDIT_ACTION_ALIASES).filter((k) => LEGACY_AUTHORITY_FILING_AUDIT_ACTION_ALIASES[k] === canonical);
    return [canonical, ...legacy];
}
//# sourceMappingURL=authority-filing-legacy-vocabulary.js.map